import React, { useEffect, useState } from 'react';
import { Button } from 'reactstrap';
import { RadioPillGroupFormHook } from '../../../../components/RadioPillGroupFormHook';
import { SelectOption } from '../../../../components/Select';
import { useForm } from 'react-hook-form';
import { RejectReason } from '../../../../entities/applicant_tracking/RejectReason';
import { LabelledSelect } from '../../../../components/LabelledSelect';
import { ApplicationService } from '../../../../services/applicant_tracking/ApplicationService';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';

interface PropTypes {
  applicationIds: number[];
  rejectReasons: RejectReason[];
  onClose: (succeeded: boolean) => void;
}

function buildOptions(options: RejectReason[]): SelectOption[] {
  return options
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((option) => ({
      label: option.name,
      value: option.id.toString(),
    }));
}

export function RejectionForm(props: PropTypes) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [rejectedBy, setRejectedBy] = useState<string>('organization');
  const [rejectReasons, setRejectReasons] = useState<SelectOption[]>([]);
  const [selectedRejectReason, setSelectedRejectReason] = useState(null);
  const { handleSubmit, resetField, control } = useForm({
    mode: 'onChange',
    defaultValues: {
      rejectedBy: rejectedBy,
    },
  });

  useEffect(() => {
    const filteredReasons = props.rejectReasons.filter(
      (reason) => reason.rejectedBy === rejectedBy,
    );
    setRejectReasons(buildOptions(filteredReasons));
    setSelectedRejectReason(null);
  }, [rejectedBy]);

  const onSubmit = handleSubmit(async () => {
    setIsLoading(true);
    try {
      props.applicationIds.length === 1
        ? await ApplicationService.reject({
            applicationId: props.applicationIds[0],
            rejectReasonId: parseInt(selectedRejectReason.value, 10),
          })
        : await ApplicationService.bulkReject({
            applicationIds: props.applicationIds,
            rejectReasonId: parseInt(selectedRejectReason.value, 10),
          });

      props.onClose(true);
    } catch (error) {
      props.onClose(false);
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  });

  return (
    <>
      {isLoading && <LoadingSpinner showBackdrop />}
      <form
        onSubmit={onSubmit}
        onChange={(e) => setRejectedBy((e.target as HTMLInputElement).value)}
      >
        <RadioPillGroupFormHook
          formHookContext={{
            controllerName: 'rejectedBy',
            formControl: control,
            required: true,
          }}
          childrenClassNames='fs-6'
          resetField={resetField}
          childrenProps={[
            {
              id: 'organization',
              label: 'We rejected them',
              value: 'organization',
            },
            {
              id: 'candidate',
              label: 'They rejected us',
              value: 'candidate',
            },
          ]}
        />
        <div className='mt-4'>
          <LabelledSelect
            options={rejectReasons}
            selected={selectedRejectReason}
            label='Rejection reason'
            placeholder='Select rejection reason'
            className={'fw-normal fs-5'}
            onChange={(reason) => setSelectedRejectReason(reason)}
          />
        </div>
        <div className='d-flex mt-5 justify-content-end'>
          <Button
            className='me-2'
            color='danger'
            onClick={() => props.onClose(null)}
          >
            Cancel
          </Button>
          <Button
            color='primary'
            disabled={!rejectedBy || !selectedRejectReason}
            type='submit'
          >
            Reject
          </Button>
        </div>
      </form>
    </>
  );
}
